import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { useFormik, FormikErrors } from 'formik';
import {
  GRIEVANCE_MILESTONES_CODES,
  GRIEVANCE_STATUSES,
  GrievanceMilestone,
  GrievancePhase,
} from '../../../../services/types/Grievance';
import UIKitButton from '../../../../shared/UIKITComponents/Button';
import UIKitDatepicker from '../../../../shared/UIKITComponents/DatepickerCustom';
import UIKitDropDown from '../../../../shared/UIKITComponents/DropDown';
import { MilestonesRowProps } from './MilestonesRowProps';
import Context from '../context/DetailsContext';
import UIKitConfirmModal from '../../../../shared/UIKITComponents/ConfirmModal';

interface ApplicabilityMilestoneFormValues extends GrievancePhase {
  isApplicable?: boolean;
}

const boolToOptionId = (value: boolean | undefined) => {
  if (value === true) {
    return 1;
  }
  if (value === false) {
    return 2;
  }
  return 0;
};

const ApplicabilityMilestoneRow = (props: MilestonesRowProps) => {
  const { isEditable, phase, onPhaseApply, validate } = props;
  const { grievancePhaseData, grievanceNumber } = useContext(Context);
  const { t } = useTranslation();
  const [showConfirmNotApplicableModal, setShowConfirmNotApplicableModal] = useState(false);

  const formik = useFormik<ApplicabilityMilestoneFormValues>({
    initialValues: {
      ...phase,
      isApplicable: grievancePhaseData.isApplicable,
    } as ApplicabilityMilestoneFormValues,
    validate: (values) => {
      if (grievancePhaseData.currentStatus !== GRIEVANCE_STATUSES.IN_PROGRESS) {
        return {};
      }

      const errors: FormikErrors<ApplicabilityMilestoneFormValues> = { ...validate(values) };

      // El validate de este formulario siempre puebla `milestones` con un error por milestone
      // (nunca un string suelto a nivel de array completo), aunque FormikErrors<T> tipe el campo
      // como union con string/string[] por soportar ambos casos de forma genérica.
      const setMilestoneDateError = (i: number) => {
        if (!errors.milestones) {
          errors.milestones = [];
        }
        const milestoneErrors = errors.milestones as FormikErrors<GrievanceMilestone>[];
        milestoneErrors[i] = {
          ...milestoneErrors[i],
          date: t('grievanceMilestoneValidations.requiredMilestone', {
            milestoneName: phase.milestones[i].name,
          }),
        };
      };

      values.milestones.forEach((m, i) => {
        if (values.isApplicable === false && !m.date) {
          setMilestoneDateError(i);
        } else if (
          values.isApplicable === true &&
          !m.date &&
          values.milestones[i].code ===
            GRIEVANCE_MILESTONES_CODES.CONFIRMATION_OF_REGISTRY_TO_COMPLAINANT
        ) {
          setMilestoneDateError(i);
        }
      });

      const allMilestonesCompleted = values.milestones.every((m) => m.date !== null);

      if (
        allMilestonesCompleted &&
        (values.isApplicable === undefined || values.isApplicable === null)
      ) {
        errors.isApplicable = t('grievanceMilestoneValidations.isApplicableRequired');
      }

      return errors;
    },

    enableReinitialize: true,
    onSubmit: (values) => {
      if (values.isApplicable === false) {
        setShowConfirmNotApplicableModal(true);
      } else {
        onPhaseApply(formik.values, {
          ...grievancePhaseData,
          isApplicable: formik.values.isApplicable,
        });
      }
    },
  });

  const applicabilityOptions = [
    { id: 1, text: t('yes'), value: 'true' },
    { id: 2, text: t('no'), value: 'false' },
  ];

  const buildMilestone = (milestonceCode: string) => {
    const milestoneIndex = phase.milestones.findIndex((m) => m.code === milestonceCode);
    if (milestoneIndex === -1) {
      return null;
    }

    const milestone = phase.milestones[milestoneIndex];

    const hasError =
      formik.errors &&
      formik.errors.milestones &&
      formik.errors.milestones[milestoneIndex] &&
      typeof formik.errors.milestones[milestoneIndex] === 'object' &&
      typeof formik.errors.milestones[milestoneIndex].date === 'string';

    return (
      <UIKitDatepicker
        name={`milestones[${milestoneIndex}].date`}
        // htmlId (no name, que es el path del campo de Formik) escopa el id/
        // aria-labelledby real del DOM por fase - sin esto, dos filas de fase
        // distintas expandidas a la vez terminan con el mismo id de input,
        // rompiendo aria-labelledby en la que se renderiza después (bug real
        // de accesibilidad, Fase 5/F7-EXIT).
        htmlId={`milestones[${phase.phaseId}][${milestoneIndex}].date`}
        label={milestone.name}
        selectedDate={
          formik.values.milestones[milestoneIndex].date
            ? new Date(formik.values.milestones[milestoneIndex].date)
            : null
        }
        onChange={(date) => formik.setFieldValue(`milestones[${milestoneIndex}].date`, date)}
        onBlur={formik.handleBlur}
        readonly={!isEditable}
        error={!!hasError}
        helperText={
          hasError
            ? (formik.errors.milestones![milestoneIndex] as FormikErrors<GrievanceMilestone>).date
            : null
        }
      />
    );
  };

  useEffect(() => () => formik.resetForm(), [grievanceNumber]);

  return (
    <form className="grv-phases-form" onSubmit={formik.handleSubmit}>
      <Grid
        container
        direction="row"
        spacing={2}
        sx={{
          justifyContent: 'flex-start',
        }}
      >
        <Grid size={4}>
          {buildMilestone(GRIEVANCE_MILESTONES_CODES.CONFIRMATION_OF_REGISTRY_TO_COMPLAINANT)}
        </Grid>
        <Grid size={4}>
          <UIKitDropDown
            label={t('isApplicable')}
            elements={applicabilityOptions}
            value={boolToOptionId(formik.values.isApplicable)}
            onChange={(id) => {
              const val = applicabilityOptions.find((o) => o.id === id);
              formik.setFieldValue('isApplicable', val ? val.value === 'true' : false);
            }}
            disabled={!isEditable}
            error={formik.errors && Boolean(formik.errors.isApplicable)}
            helperText={formik.errors && formik.errors.isApplicable}
            className="grv-milestone-dropdown"
          />
        </Grid>
        <Grid size={4}>
          {buildMilestone(GRIEVANCE_MILESTONES_CODES.APPLICABILITY_TO_COMPLAINANT)}
        </Grid>
        {isEditable && (
          <Grid className="grv-milestone-submit-btn">
            <UIKitButton
              text={t('apply')}
              type="submit"
              className="btn-primary"
              disabled={!formik.isValid || !formik.dirty}
            />
          </Grid>
        )}
      </Grid>
      {showConfirmNotApplicableModal && (
        <UIKitConfirmModal
          open={showConfirmNotApplicableModal}
          submitText={t('apply')}
          message={t('grievanceNotApplicableConfirmMessage')}
          onClose={() => setShowConfirmNotApplicableModal(false)}
          onConfirm={() => {
            onPhaseApply(formik.values, { ...grievancePhaseData, isApplicable: false });
          }}
        />
      )}
    </form>
  );
};

export default ApplicabilityMilestoneRow;
